import { useAuth } from 'oidc-react';
import React, { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { environment } from './environments/environment';
import { Route, Switch, useLocation } from 'react-router-dom';
import { RootState, useAppThunkDispatch } from './redux/store';
import { useSelector } from 'react-redux';
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown';
import { setLoggedInUser, setErrorInfo } from './redux/common-data-slice';
import { Box, LinearProgress } from '@mui/material';
import OidcNavbarComponent from './components/navbar/oidc-navbar.component';
import { Panel, PanelGroup } from 'react-resizable-panels';
import SlidebarComponent from './components/navbar/slidebar.component';
import Helper from './utils/helper';
import OktaLoginComponent from './components/okta/okta-login.component'
import PageNotFoundComponent from './components/homepage/page-not-found.component'
import HomePageComponent from './components/homepage/home-page.component';
import { oktaSignInConfig } from './components/okta/config';
import TaskListComponent from './components/tasks/tasks.component'
import axios from 'axios';
import FHIRPatientCallbackComponent from './components/fhir/fhir-patient-callback.component';
import PlanComponent from './components/patientRole/plan/plan.component'
import ActivityComponent from './components/patientRole/activity/activity.component'
import EducationComponent from './components/patientRole/education/education.component'
import MessageComponent from './components/patientRole/message/message.component'
import LoginOIDCCodeComponent from './components/okta/login-oidc-code.component';

const DetailContainerComponent = lazy(
  () => import('./components/patients/detail/detail-container.component')
)
const QuestionnaireComponent = lazy(
  () => import('./components/questionnaires/questionnaire.component')
)
const PatientTreatmentPlanComponent = lazy(
  () => import('./components/patients/treatment-plan.component')
)
const QuestionnaireListComponent = lazy(
  () => import('./components/patients/questionnaire-list.component')
)
const ProviderManagerComponent = lazy(
  () => import('./components/providers/ProviderManager')
)
const SiteManagerComponent = lazy(
  () => import('./components/sites/SiteManager')
)
const EncountersDashboardComponent = lazy(
  () => import('./components/encounters/EncountersDashboard')
)

// Keys to clear from both localStorage and sessionStorage on OIDC logout/401
const OIDC_STORAGE_KEYS = [
  'picklistValues',
  'encryptedToken',
  'encryptedTokenTS',
  'sessionUserInfo',
  'tokenExpiry',
  'oidcRedirectPath',
  'id_token',
  'id_token_expires_in',
  'access_token',
  'refresh_token',
  'scope',
  'token_type',
];

const clearOidcStorage = (): void => {
  OIDC_STORAGE_KEYS.forEach(key => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  // Clear oidc-react / oidc-client-ts internal user storage key
  // Format: oidc.user:<authority>:<clientId>
  const oidcLibKey = `oidc.user:${environment.oidc.authority}:${environment.oidc.clientId}`;
  sessionStorage.removeItem(oidcLibKey);
  localStorage.removeItem(oidcLibKey);

  // Note: userLoggedOut is intentionally NOT set here. This function is called on forced
  // 401 logouts (backend restart). Without the flag, the /login page auto-redirects to
  // Azure AD. Only OidcLogoutComponent (intentional logout) sets userLoggedOut = 'true'
  // to show the "Sign In" button instead.
};

// Module-level flag to prevent concurrent 401 redirects
let isOidcSigningOut = false;

// Decode the JWT exp claim to check actual token expiry.
// More reliable than our custom sessionStorage.tokenExpiry because it works
// even when sessionStorage is cleared (new tab, forced logout, etc.).
const isAzureTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // payload.exp is in seconds since epoch; Date.now() is in milliseconds
    return Date.now() / 1000 > payload.exp;
  } catch {
    return true; // Treat undecodable token as expired
  }
};

/**
 * OIDC-specific axios interceptor setup.
 * Uses useLayoutEffect to register interceptors BEFORE children's useEffect fires,
 * ensuring the Authorization header is present on the very first API calls.
 * Unlike the Okta-based SetupAxios, this doesn't require an Okta Security context.
 */
const OIDCSetupAxios: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppThunkDispatch();
  const accessToken = useSelector((state: RootState) => state.auth.token);
  const errorInfo = useSelector((state: RootState) => state.commonData.errorInfo);

  useLayoutEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config: any) => {
        const rawToken = localStorage.getItem('access_token');
        const isAuthRoute = window.location.pathname === '/login' || window.location.pathname === '/login/callback';

        if (!rawToken) {
          if (isAuthRoute) return Promise.reject(new Error('Not authenticated'));
          return config;
        }

        config.headers.Accept = 'application/json';
        if (!config.headers['Content-Type']) {
          config.headers['Content-Type'] = 'application/json';
        }

        // For Azure AD (OIDC mode), the backend validates the raw Azure AD JWT directly
        // via validate_access_token() — no encrypted token exchange needed.
        config.headers.Authorization = `Bearer ${rawToken}`;
        return config;
      },
      (error: any) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response: any) => response,
      (error: any) => {
        if (error?.response?.status === 401) {
          const isAuthRoute = window.location.pathname === '/login' || window.location.pathname === '/login/callback';
          const storedToken = localStorage.getItem('access_token');
          // Use the actual JWT exp claim — not sessionStorage.tokenExpiry — so this
          // works correctly even when sessionStorage is empty (new tab, forced logout, etc.)
          const tokenIsValid = !!(storedToken && !isAzureTokenExpired(storedToken));

          if (!isOidcSigningOut && !isAuthRoute && !tokenIsValid) {
            // Azure AD token is genuinely gone/expired — full re-auth
            isOidcSigningOut = true;
            clearOidcStorage();
            window.location.href = '/login';
          }
          // If token is still valid, the 401 is a backend-side rejection (e.g., backend
          // restarted). Log the error but do NOT redirect — let the API call fail normally.
        } else if (error?.code === 'ERR_NETWORK') {
          dispatch(setErrorInfo({ status: 'ERR_NETWORK', msg: '' }));
        } else if (errorInfo?.status !== '') {
          dispatch(setErrorInfo({ status: '', msg: '' }));
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
      isOidcSigningOut = false; // Reset on unmount so next session works
    };
  }, [accessToken]);

  return <>{children}</>;
};

const hasUserLoggedOutIntentionally = (): boolean => {
  return sessionStorage.getItem('userLoggedOut') === 'true'
}

const clearLogoutFlag = (): void => {
  sessionStorage.removeItem('userLoggedOut')
}

const OIDCAuthWrapper = () => {
  const auth = useAuth() as any;
  const location = useLocation();
  const dispatch = useAppThunkDispatch();
  const loggedInUser = useSelector((state: RootState) => state.commonData.loggedInUser);

  const [isAuthReady, setIsAuthReady] = useState(false);
  const initializationDone = useRef(false);

  const currentPath = location.pathname + location.search;
  const isLoginRoute = location.pathname === '/login' || location.pathname === '/login/callback';
  const isUserLoggedOut = hasUserLoggedOutIntentionally();

  const hasValidOIDCCache = (): boolean => {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken || isAzureTokenExpired(accessToken)) {
      return false;
    }
    // Token exists and is not expired. Require sessionUserInfo so we can
    // populate the user profile in Redux without an extra auth round-trip.
    // sessionStorage is always present after login-oidc-code sets it; it only
    // goes missing if the user opens a new tab or sessionStorage is wiped —
    // in that case the OIDC library re-authenticates silently via Azure AD SSO.
    return !!window.sessionStorage.getItem('sessionUserInfo');
  };

  // ---------------------------------------------------------------------------
  // NO session-inactivity adapter is registered here, DELIBERATELY (RD 2026-08-06).
  //
  // `SessionInactivityGuard` is mounted at the app root and would drive a HIPAA idle
  // countdown for this branch too if this component registered a `SessionAuthAdapter`.
  // It does not. This branch serves a vendor-contracted deployment (RD 2026-08-06), so
  // its session behaviour is governed by that agreement rather than by our provider-app
  // policy. It has never had idle auto-logoff; switching one on here would change a
  // contracted surface without anyone having asked for it.
  //
  // The guard is inert without an adapter, so behaviour here is unchanged. Registering
  // one is the whole change required if idle logoff is ever wanted for Azure — see
  // `useRegisterSessionAuth` in components/session/SessionInactivityGuard.tsx.
  //
  // This absence is a decision, not an oversight. Do not "fix" it without a disposition.
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!isAuthReady && !hasValidOIDCCache() && !isLoginRoute && currentPath !== '/') {
      sessionStorage.setItem('oidcRedirectPath', currentPath);
    }
  }, [isAuthReady, auth.userData?.access_token, isLoginRoute]);

  // Initialize user from cache or auth provider (ONE TIME ONLY)
  useEffect(() => {
    // Prevent multiple initializations
    if (initializationDone.current) {
      return;
    }

    // If on login callback route, let LoginOIDCCodeComponent handle everything
    if (location.pathname === '/login/callback') {
      setIsAuthReady(true);
      initializationDone.current = true;
      return;
    }

    // Check for cached valid token — valid tokens take priority over the logout flag.
    // This prevents the "signed out" page from appearing after a dev server restart
    // when the user still has a valid session.
    if (hasValidOIDCCache()) {
      const sessionUserInfo = window.sessionStorage.getItem('sessionUserInfo');
      if (sessionUserInfo && !loggedInUser.email) {
        try {
          const userInfo = JSON.parse(sessionUserInfo);
          dispatch(setLoggedInUser(userInfo));
        } catch (e) {
          console.error('Failed to parse sessionUserInfo:', e);
        }
      }
      clearLogoutFlag(); // Clear any stale logout flag since we have valid tokens
      setIsAuthReady(true);
      initializationDone.current = true;
      return;
    }

    // If user intentionally logged out (and no valid cache), show login page
    if (isUserLoggedOut) {
      setIsAuthReady(true);
      initializationDone.current = true;
      return;
    }

    // Check if auth provider has loaded
    if (!auth.isLoading) {
      // If we have auth data, use it
      if (auth.userData?.access_token) {
        if (!loggedInUser.email) {
          const userInfo = { ...auth.userData?.profile } as any;
          dispatch(setLoggedInUser(userInfo));
        }
        setIsAuthReady(true);
        initializationDone.current = true;
      } else if (!isLoginRoute) {
        // Not authenticated and not on login route - redirect to login
        initializationDone.current = true;
        window.location.href = '/login';
      } else {
        // On login route
        setIsAuthReady(true);
        initializationDone.current = true;
      }
    }
  }, [auth.userData?.access_token, auth.isLoading, loggedInUser.email, dispatch, isLoginRoute, location.pathname]);

  // =========================================================================
  // RENDER: Loading state during authentication
  // =========================================================================
  if (!isAuthReady && auth.isLoading && !isLoginRoute) {
    return (
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <LinearProgress sx={{ width: '300px' }} />
        <div style={{ marginLeft: '10px' }}>Authenticating...</div>
      </Box>
    );
  }

  // Render the app when ready
  if (isAuthReady || isLoginRoute) {
    return (
      <>
        <OidcNavbarComponent
          history={''}
          location={window.location}
          match={''}
        />
        <GetOIDCRoute />
      </>
    );
  }

  return null;
};

// Separate component for OIDC routes
const GetOIDCRoute = () => {
  const url = useLocation();
  const patientDetailStatus: any = useSelector((state: RootState) => state.patientDetail.status);
  const panelRef = useRef(null) as any;
  const [defaultMenuSize, setDefaultMenuSize] = useState(2.9);

  useEffect(() => {
    const handleWindowResize = () => {
      if (panelRef?.current) {
        if (window.innerWidth < 1200) {
          panelRef.current.collapse();
          setDefaultMenuSize(0);
        } else {
          panelRef.current.expand();
          setDefaultMenuSize(2.9);
        }
      }
    };

    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  const getTaskPanel = () => {
    const tabs = [
      '/patient-list',
      '/',
      '/all-tasks',
      '/my-tasks',
      '/top-tasks',
      '/clinic-visits',
      '/providers',
      '/sites',
      '/rpm-dashboard',
      '/encounters'
    ];

    return (
      <PanelGroup autoSaveId="example" direction="horizontal">
        {/* SIDEBAR PANEL */}
        {patientDetailStatus !== 'loading' && (
          <Panel
            ref={panelRef}
            collapsedSize={3.4}
            minSize={defaultMenuSize > 0 ? 3.4 : 0.5}
            maxSize={defaultMenuSize > 0 ? 3.4 : 0.5}
            style={{ overflow: 'visible' }}
          >
            <Box sx={{
              position: 'relative',
              minHeight: '20rem',
              height: '100%',
              overflow: 'visible'
            }}>
              {/* Sidebar visible */}
              {defaultMenuSize > 0 && (
                <SlidebarComponent
                  defaultMenuSize={defaultMenuSize}
                  isPatient={false}
                  onToggle={() => setDefaultMenuSize(defaultMenuSize <= 0 ? 3.4 : 0)}
                />
              )}

              {/* Sidebar collapsed - show expand icon */}
              {defaultMenuSize === 0 && (
                <Box sx={{
                  borderRight: '0.12rem solid #e1e1e1',
                  height: '100%',
                  width: '100%'
                }}>
                  <ExpandCircleDownIcon
                    onClick={() => setDefaultMenuSize(3.4)}
                    sx={{
                      position: 'absolute',
                      left: tabs.includes(window.location.pathname) ? '0.15rem' : '0rem',
                      top: tabs.includes(window.location.pathname) ? '0.8rem' : '0.45rem',
                      fontSize: '1rem !important',
                      fill: 'grey !important',
                      cursor: 'pointer !important',
                      zIndex: 1000,
                      transform: 'rotate(270deg) !important',
                      '&:hover': {
                        fill: 'var(--color-primary) !important',
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
          </Panel>
        )}

        {/* MAIN CONTENT PANEL */}
        <Panel style={{
          borderLeft: url.pathname.endsWith('/details') ? '0.12rem solid #e1e1e1' : ''
        }}>
          <Suspense fallback={<div>Loading...</div>}>
            <OIDCSetupAxios>
              <Switch>
                {/* HOME / DEFAULT ROUTE */}
                <Route
                  exact
                  path="/"
                  render={() => Helper.isAIDA()
                    ? <HomePageComponent history={undefined} location={undefined} match={undefined} />
                    : <TaskListComponent />
                  }
                />

                {/* TASK ROUTES */}
                <Route exact path="/top-tasks" component={TaskListComponent} />
                <Route exact path="/my-tasks" component={TaskListComponent} />
                <Route exact path="/all-tasks" component={TaskListComponent} />

                {/* PATIENT ROUTES */}
                <Route exact path="/patient-list" component={HomePageComponent} />
                <Route exact path="/clinic-visits" component={HomePageComponent} />

                {/* PATIENT DETAIL ROUTES */}
                <Route
                  component={QuestionnaireComponent}
                  exact
                  path="/patients/:patientId/questionnaires/:questionnaireId"
                />
                <Route
                  component={DetailContainerComponent}
                  exact
                  path="/patients/:patientId/details"
                />
                <Route
                  component={QuestionnaireListComponent}
                  exact
                  path="/patients/:patientId/list"
                />
                <Route
                  component={PatientTreatmentPlanComponent}
                  exact
                  path="/patients/:patientId/treatment-plan"
                />

                {/* FHIR CALLBACK ROUTE */}
                <Route
                  exact
                  path="/epic-fhir/sync/:fhirPatientId/launch-callback"
                  component={FHIRPatientCallbackComponent}
                />

                {/* ADMIN ROUTES */}
                <Route exact path="/providers" component={ProviderManagerComponent} />
                <Route exact path="/sites" component={SiteManagerComponent} />

                {/* ENCOUNTERS ROUTE */}
                <Route exact path="/encounters" component={EncountersDashboardComponent} />

                {/* PATIENT ROLE ROUTES */}
                <Route component={PlanComponent} exact path="/plan" />
                <Route component={ActivityComponent} exact path="/activity" />
                <Route component={EducationComponent} exact path="/education" />
                <Route component={MessageComponent} exact path="/message" />

                {/* AUTH ROUTES */}
                <Route
                  exact
                  path="/login"
                  render={() => (
                    <OktaLoginComponent
                      history={''}
                      location={window.location}
                      match={''}
                      config={oktaSignInConfig}
                    />
                  )}
                />
                <Route
                  path="/login/callback"
                  render={() => (
                    <LoginOIDCCodeComponent
                      history={''}
                      location={window.location}
                      match={''}
                      config={oktaSignInConfig}
                    />
                  )}
                />

                {/* 404 ROUTE - MUST BE LAST */}
                <Route path="*" component={PageNotFoundComponent} />
              </Switch>
            </OIDCSetupAxios>
          </Suspense>
        </Panel>
      </PanelGroup>
    );
  };

  return getTaskPanel();
};

export default OIDCAuthWrapper;